import { useYetiSwapNFTCollectionContract } from './useContract'
import { useCallback } from 'react'
import { calculateGasMargin } from '../utils'
import { TransactionResponse } from '@ethersproject/providers'
import { useActiveWeb3React } from './index'
import { useTransactionAdder } from '../state/transactions/hooks'
import { useSingleCallResult } from '../state/multicall/hooks'
import { ChainId, JSBI, TokenAmount } from '@yetiswap/sdk'
import { YTS } from '../constants'
import { useAddPopup } from '../state/application/hooks'

export enum MintNFTState {
  NOT_APPROVED,
  PENDING,
  APPROVED
}

export function useMintNFT(fee: number): { mintNFT: (fileHash: string | null) => Promise<any> } {
  const creatorFee = fee ? fee : '0'
  const contract = useYetiSwapNFTCollectionContract()
  const { account } = useActiveWeb3React()
  const addTransaction = useTransactionAdder()
  const addPopup = useAddPopup()

  const mintNFT = useCallback(
    async (fileHash: string | null): Promise<any> => {
      if (!contract || !account || !fileHash) return
      const args = [account, creatorFee, fileHash]

      return await contract.estimateGas['mintNFT'](...args, {}).then(estimatedGasLimit => {
        return contract
          .mintNFT(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
          .then((response: TransactionResponse) => {
            addTransaction(response, {
              summary: 'NFT Minted Successful'
            })
            return response.wait(1)
          }).catch((error: any) => {
            addPopup({
              txn: { hash: '', success: false, summary: 'NFT Mint failed', linkRemove: true }
            })
          })
      }).catch((error: any) => {
        addPopup({
          txn: { hash: '', success: false, summary: 'NFT Mint failed', linkRemove: true }
        })
      })
    },
    [creatorFee, contract, addTransaction, account]
  )

  return { mintNFT }
}

export function useGetBaseNFTMintFeePrice(): number {
  const contract = useYetiSwapNFTCollectionContract()
  const baseNftMintFeePriceResult = useSingleCallResult(contract, 'baseNftMintFeePrice')
  let result = 0

  if (!baseNftMintFeePriceResult.loading && baseNftMintFeePriceResult.result) {
    result = parseInt(JSBI.BigInt(baseNftMintFeePriceResult.result?.[0]).toString(), 0)
  }
  return result
}

export function useGetBaseUri(): string {
  const contract = useYetiSwapNFTCollectionContract()
  const baseUriResult = useSingleCallResult(contract, 'baseURI')
  let result = ''

  if (!baseUriResult.loading && baseUriResult.result) {
    result = baseUriResult.result?.[0]
  }
  return result
}

export function useGetMaxCreatorFeeRate(): number {
  const contract = useYetiSwapNFTCollectionContract()
  const maxCreatorFeeRateResult = useSingleCallResult(contract, 'maxCreatorFeeRate')
  let result = 0

  if (!maxCreatorFeeRateResult.loading && maxCreatorFeeRateResult.result) {
    result = parseInt(maxCreatorFeeRateResult.result?.[0])
  }
  return result
}

export function useCalculateMintFee(baseMintFee: number, royalties: number): TokenAmount {
  const { chainId } = useActiveWeb3React()
  const yts = chainId ? YTS[chainId] : YTS[ChainId.AVALANCHE]
  const mintFee = JSBI.multiply(JSBI.BigInt(baseMintFee), JSBI.BigInt(royalties))
  return new TokenAmount(yts, mintFee)
}

export function useGetCreators(tokenID: number) {
  const contract = useYetiSwapNFTCollectionContract()
  const creatorsResult = useSingleCallResult(contract, 'creators', [tokenID])
  let result = ''

  if (!creatorsResult.loading && creatorsResult.result) {
    result = creatorsResult.result?.[0]
  }
  return result
}

export function useGetCreatorFeeRate(tokenID: number) {
  const contract = useYetiSwapNFTCollectionContract()
  const creatorsResult = useSingleCallResult(contract, 'creatorsFeeRate', [tokenID])
  let result = 0

  if (!creatorsResult.loading && creatorsResult.result) {
    result = parseInt(JSBI.BigInt(creatorsResult.result[0]).toString(), 0)
  }
  return result
}
